import api from './api'
import type { ApiResponse } from '../types'

// ================================================================================
// TRANSLATION SERVICE — admin per-language content translation API.
//
// The backend exposes, per content type, three admin-only endpoints:
//   GET    {basePath}/{id}/translations        → TranslationRow[]   (all languages)
//   PUT    {basePath}/{id}/translations/{lang}  → TranslationRow      (upsert; body below)
//   DELETE {basePath}/{id}/translations/{lang}  → void
// where {basePath} is the content collection, e.g. '/knowledge/articles', '/resources',
// '/testimonials', '/facilities'. English ("en") is the canonical parent row — the backend
// rejects upserting "en" (edit the item's own fields directly instead).
//
// TranslationRow is a superset of every content type's translatable fields; only the fields
// relevant to a given content type are populated (the rest are null/undefined). This avoids four
// near-identical DTOs on both sides.
// ================================================================================

/** Superset of every content type's translatable fields. */
export interface TranslationRow {
  language: string
  // KnowledgeArticle
  title?: string
  content?: string
  excerpt?: string
  // Resource
  description?: string
  sourceOrg?: string
  // Testimonial
  body?: string
  // Facility
  name?: string
  address?: string
  district?: string
  state?: string
  servicesOffered?: string
  operatingHours?: string
  source?: string
}

/** Admin upsert body — same superset shape; send only the fields relevant to the content type. */
export type TranslationUpsertRequest = TranslationRow

/** All languages the portal publishes content in (mirrors backend LanguageResolver). */
export const CONTENT_LANGUAGES = ['en', 'hi', 'mr'] as const
export type ContentLanguage = (typeof CONTENT_LANGUAGES)[number]

/** Native display name for a content language code. */
export const CONTENT_LANGUAGE_NAMES: Record<string, string> = {
  en: 'English',
  hi: 'हिन्दी',
  mr: 'मराठी',
}

/** List all language translations for one content item (admin). */
export async function listTranslations(basePath: string, id: number): Promise<TranslationRow[]> {
  const res = await api.get<ApiResponse<TranslationRow[]>>(`${basePath}/${id}/translations`)
  return res.data.data
}

/** Upsert (create or update) one language's translation for one content item (admin). */
export async function upsertTranslation(
  basePath: string,
  id: number,
  lang: string,
  body: TranslationUpsertRequest
): Promise<TranslationRow> {
  const res = await api.put<ApiResponse<TranslationRow>>(`${basePath}/${id}/translations/${lang}`, body)
  return res.data.data
}

/** Delete one language's translation for one content item (admin). */
export async function deleteTranslation(basePath: string, id: number, lang: string): Promise<void> {
  await api.delete<ApiResponse<void>>(`${basePath}/${id}/translations/${lang}`)
}